import React from "react";
import { toast } from "sonner";

/**
 * Official "Get it on Google Play" badge.
 *
 * The Adukoo Android app is live at
 *   https://play.google.com/store/apps/details?id=com.adukootech.adukoo
 *
 * We deep-link with UTM params so the install attribution shows up in
 * Play Console acquisition reports — that's how we measure how many web
 * visitors actually install the app from each surface (header, hero,
 * footer, catalog, etc.).
 *
 * The badge artwork itself is the official Google asset from
 *   /marketing-assets/playstore/badges/google-play-badge-en.png
 * cached locally so it loads instantly with the rest of the page.
 *
 * v79.2 — iOS Safari awareness.
 * v79.3 — Desktop awareness (Mac Chrome 404 fix). The Play Store
 * listing is only meaningful on Android, so we now only navigate on
 * Android. iOS users get a "use the web app" toast, and Mac/Windows/
 * Linux desktops get a "use the web app" toast. This eliminates the
 * intermittent "We're sorry, the requested URL was not found on this
 * server" page Mac Chrome was occasionally showing for the Play listing.
 *
 * Variants:
 *   • "lg"     — full-size badge (~180 × 56 px). Use in hero rows.
 *   • "md"     — compact (~150 × 46 px). Use in footer / catalog.
 *   • "sm"     — tiny badge (~130 × 40 px). Use in mobile header.
 *   • "ghost"  — outlined yellow-on-navy pill matching the rest of the
 *               site nav (no PNG). Use when the dark PNG would clash
 *               with surrounding chrome (e.g. inline with `<Header>`).
 */
export const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.adukootech.adukoo";

export function buildPlayUrl(source) {
    const params = new URLSearchParams({
        utm_source: "adukoo_web",
        utm_medium: "badge",
        utm_campaign: "playstore_install",
        utm_content: source || "site",
    });
    return `${PLAY_STORE_URL}&${params.toString()}`;
}

/**
 * Detect the device class so we can route the badge tap correctly:
 *   • Android   → let the link open the Play Store listing (works).
 *   • iOS       → polite "use the Adukoo web app on adukoo.com" toast + redirect.
 *   • Anything else (Mac/Windows/Linux desktop, ChromeOS) → polite
 *     "Available on Android — use the web app on desktop" toast.
 *
 * We previously only intercepted iOS, but Mac Chrome was occasionally
 * showing Google's "We're sorry, the requested URL was not found on this
 * server" page when sent to the Play listing — most likely a regional
 * / session-state quirk on the desktop Play Store UI. The reliable fix
 * is to only navigate on the platform where the listing is meaningful
 * (Android), and otherwise steer the user to the fully-functional web
 * app at adukoo.com.
 *
 * iPadOS 13+ masquerades as `MacIntel` desktop Safari, so we additionally
 * sniff `maxTouchPoints` per Apple's documented workaround
 * (https://developer.apple.com/forums/thread/119186) to catch tablet
 * users who would otherwise be classified as desktop.
 */
function detectPlatform() {
    if (typeof navigator === "undefined") return "other";
    const ua = navigator.userAgent || navigator.vendor || "";
    if (/android/i.test(ua)) return "android";
    if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "ios";
    if (
        navigator.platform === "MacIntel" &&
        typeof navigator.maxTouchPoints === "number" &&
        navigator.maxTouchPoints > 1
    ) {
        return "ios"; // iPadOS 13+ disguised as Mac
    }
    return "other";
}

const SIZE = {
    lg: { h: 56, label: "h-14" },
    md: { h: 46, label: "h-[46px]" },
    sm: { h: 40, label: "h-10" },
};

function handleClick(e, source) {
    const platform = detectPlatform();
    if (platform === "android") return; // Let the <a> open Play Store
    e.preventDefault();
    const isIOSDevice = platform === "ios";
    // v79.84 — Live on Google Play. iOS users are directed to install
    // the web app via "Add to Home Screen"; there's no App Store build.
    toast.info(
        isIOSDevice ? "Use Adukoo web app on iPhone 🍎" : "Adukoo is live on Google Play 📱",
        {
            description: isIOSDevice
                ? "Open adukoo.com in Safari, then tap Share → Add to Home Screen. You get the full app experience — no App Store install needed."
                : "Open this page on your Android phone to install from Google Play. Meanwhile the full Adukoo marketplace is right here on the web — keep browsing.",
            duration: 6500,
            action: {
                label: "Open web app",
                onClick: () => {
                    window.location.href = "/";
                },
            },
            id: `playstore-toast-${source}-${platform}`,
        },
    );
}

export default function PlayStoreBadge({ source = "site", variant = "md", className = "" }) {
    const href = buildPlayUrl(source);
    const testId = `play-store-badge-${source}`;

    if (variant === "ghost") {
        return (
            <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => handleClick(e, source)}
                data-testid={testId}
                aria-label="Get the Adukoo app on Google Play"
                title="Get the Adukoo Android app on Google Play"
                className={`inline-flex items-center gap-2 h-10 px-3.5 rounded-full bg-aduku-yellow text-aduku-navy hover:bg-aduku-yellow-600 font-bold text-xs sm:text-sm shadow-sm transition-colors ${className}`}
            >
                <svg width="16" height="16" viewBox="0 0 512 512" aria-hidden="true">
                    {/* Stylised Play triangle in the Adukoo navy. */}
                    <path d="M325.3 234.3L104.4 39.3c-5.3-4.7-13.4-1-13.4 6.2v420.9c0 7.2 8.1 10.9 13.4 6.2l220.9-195a8.4 8.4 0 0 0 0-12.6z" fill="currentColor"/>
                    <path d="M407.9 222.4l-58.9-32.3-66.7 65.9 66.7 65.9 58.9-32.3a36 36 0 0 0 0-67.2z" fill="currentColor" opacity="0.55"/>
                </svg>
                <span className="leading-tight text-left">
                    <span className="block text-[9px] uppercase tracking-[0.18em] font-extrabold opacity-80">Get it on</span>
                    <span className="block text-sm font-black -mt-0.5">Google Play</span>
                </span>
            </a>
        );
    }

    const { h, label } = SIZE[variant] || SIZE.md;
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => handleClick(e, source)}
            data-testid={testId}
            aria-label="Get the Adukoo app on Google Play"
            title="Get the Adukoo Android app on Google Play"
            className={`inline-flex ${label} hover:opacity-90 transition-opacity ${className}`}
        >
            <img
                src="/marketing-assets/playstore/badges/google-play-badge-en.png"
                alt="Get it on Google Play"
                height={h}
                style={{ height: `${h}px`, width: "auto", display: "block" }}
                loading="lazy"
                decoding="async"
            />
        </a>
    );
}
